// @ts-strict-ignore
import { DashboardCard } from "@dashboard/components/Card";
import MediaTile from "@dashboard/components/MediaTile";
import { ProductMediaFragment, ProductMediaType } from "@dashboard/graphql";
import { ReorderAction } from "@dashboard/types";
import createMultiFileUploadHandler from "@dashboard/utils/handlers/multiFileUploadHandler";
import { PlusIcon } from "@saleor/macaw-ui";
import { Box, Button, Dropdown, List, Skeleton, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { SortableContainer, SortableElement } from "react-sortable-hoc";

import { messages } from "./messages";

interface MediaItemProps {
  media: ProductMediaFragment;
  editHref: string;
  onDelete: () => void;
  isMain?: boolean;
}

const SortableMediaItem = SortableElement<MediaItemProps>(({ media, editHref, onDelete, isMain }: MediaItemProps) => (
  <Box
    style={{
      gridColumn: isMain ? "span 2" : "span 1",
      gridRow: isMain ? "span 2" : "span 1",
      width: isMain ? "312px" : "148px",
      height: isMain ? "312px" : "148px",
      position: "relative",
    }}
  >
    {isMain && (
      <Box
        style={{
          position: "absolute",
          bottom: "12px",
          left: "12px",
          backgroundColor: "#fff",
          padding: "4px 12px",
          borderRadius: "4px",
          fontSize: "12px",
          fontWeight: 600,
          zIndex: 2,
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        }}
      >
        Main
      </Box>
    )}
    <Box
      style={{
        width: "100%",
        height: "100%",
      }}
    >
      <MediaTile media={media} editHref={editHref} onDelete={onDelete} />
    </Box>
  </Box>
));

const MediaPlaceholder = ({ onClick }: { onClick: () => void }) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };

  return (
    <Box
      onClick={handleClick}
      style={{
        height: "148px",
        width: "148px",
        border: "2px dashed #ccc",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        backgroundColor: "#f9f9f9",
        transition: "all 0.2s ease",
        pointerEvents: "auto",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#999";
        e.currentTarget.style.backgroundColor = "#f5f5f5";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#ccc";
        e.currentTarget.style.backgroundColor = "#f9f9f9";
      }}
    >
      <PlusIcon
        style={{ fontSize: "32px", color: "#999" }}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      />
    </Box>
  );
};

interface SortableMediaListProps {
  media: ProductMediaFragment[];
  preview: ProductMediaFragment[];
  onDelete: (id: string) => () => void;
  getEditHref: (id: string) => string;
}

const SortableMediaList = SortableContainer<SortableMediaListProps>(
  ({ media, preview, onDelete, getEditHref }: SortableMediaListProps) => (
    <div
      style={{
        display: "contents",
      }}
    >
      {media.map((mediaObj, index) => (
        <SortableMediaItem
          key={mediaObj.id}
          index={index}
          media={mediaObj}
          editHref={getEditHref(mediaObj.id)}
          onDelete={onDelete(mediaObj.id)}
          isMain={index === 0}
        />
      ))}
      {preview
        .sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1))
        .map((mediaObj, index) => (
          <Box key={index} style={{ width: "148px", height: "148px" }}>
            <MediaTile loading={true} media={mediaObj} />
          </Box>
        ))}
    </div>
  ),
);

interface ProductMediaProps {
  media: ProductMediaFragment[];
  loading?: boolean;
  getImageEditUrl: (id: string) => string;
  onImageDelete: (id: string) => () => void;
  onImageReorder?: ReorderAction;
  onImageUpload: (file: File) => any;
  openMediaUrlModal: () => any;
}

const ProductMedia = (props: ProductMediaProps) => {
  const {
    media,
    getImageEditUrl,
    onImageDelete,
    onImageReorder,
    onImageUpload,
    openMediaUrlModal,
  } = props;
  const intl = useIntl();
  const imagesUpload = React.useRef<HTMLInputElement>(null);
  const anchor = React.useRef<HTMLButtonElement>();
  const [imagesToUpload, setImagesToUpload] = React.useState<ProductMediaFragment[]>([]);
  const handleImageUpload = createMultiFileUploadHandler(onImageUpload, {
    onAfterUpload: () => setImagesToUpload(prevImagesToUpload => prevImagesToUpload.slice(1)),
    onStart: files => {
      Array.from(files).forEach((file, fileIndex) => {
        const reader = new FileReader();

        reader.onload = event => {
          setImagesToUpload(prevImagesToUpload => [
            ...prevImagesToUpload,
            {
              __typename: "ProductMedia",
              alt: "",
              id: "",
              sortOrder: fileIndex,
              type: ProductMediaType.IMAGE,
              url: event.target.result as string,
              oembedData: null,
            },
          ]);
        };
        reader.readAsDataURL(file);
      });
    },
  });

  return (
    <DashboardCard>
      <DashboardCard.Header>
        <DashboardCard.Title>
          <FormattedMessage {...messages.media} />
        </DashboardCard.Title>
        <DashboardCard.Toolbar>
          <Dropdown>
            <Dropdown.Trigger>
              <Button
                variant="secondary"
                type="button"
                data-test-id="button-upload-image"
                ref={anchor}
                icon={
                  <PlusIcon onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                }
              >
                {intl.formatMessage(messages.addMedia)}
              </Button>
            </Dropdown.Trigger>
            <Dropdown.Content align="end">
              <List
                padding={2}
                borderRadius={4}
                boxShadow="defaultOverlay"
                backgroundColor="default1"
              >
                <Dropdown.Item>
                  <List.Item
                    borderRadius={4}
                    paddingX={1.5}
                    paddingY={2}
                    onClick={() => imagesUpload.current.click()}
                    data-test-id="upload-images"
                  >
                    <Text>{intl.formatMessage(messages.uploadImages)}</Text>
                  </List.Item>
                </Dropdown.Item>
                <Dropdown.Item>
                  <List.Item
                    borderRadius={4}
                    paddingX={1.5}
                    paddingY={2}
                    onClick={openMediaUrlModal}
                    data-test-id="upload-media-url"
                  >
                    <Text>{intl.formatMessage(messages.uploadUrl)}</Text>
                  </List.Item>
                </Dropdown.Item>
              </List>
            </Dropdown.Content>
          </Dropdown>
        </DashboardCard.Toolbar>
      </DashboardCard.Header>
      <DashboardCard.Content>
        <Box>
          <Box
            as="input"
            display="none"
            id="fileUpload"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleImageUpload(event.target.files)
            }
            multiple
            type="file"
            ref={imagesUpload}
            accept="image/*"
          />
        </Box>
        <Box>
          {media === undefined ? (
            <Box padding={5}>
              <Skeleton />
            </Box>
          ) : (
            <Box
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 148px)",
                gap: "16px",
              }}
            >
              <SortableMediaList
                media={media}
                preview={imagesToUpload}
                onDelete={onImageDelete}
                getEditHref={getImageEditUrl}
                onSortEnd={onImageReorder}
                axis="xy"
                distance={20}
                helperClass="dragged"
              />
              <MediaPlaceholder onClick={() => imagesUpload.current?.click()} />
            </Box>
          )}
        </Box>
      </DashboardCard.Content>
    </DashboardCard>
  );
};

ProductMedia.displayName = "ProductMedia";
export default ProductMedia;
